import { Project } from '../project/types';

/**
 * Snapshot-based undo/redo over the project document. The document is small
 * and fully serializable, so whole-document snapshots are cheap and
 * bulletproof. Consecutive rapid mutations in the same "group" (e.g. one
 * drag gesture) coalesce into a single undo step.
 */
export class UndoManager {
  private past: Project[] = [];
  private future: Project[] = [];
  private lastGroup: string | null = null;
  private lastTime = 0;
  private readonly cap = 100;
  private readonly coalesceMs = 800;

  /** Record the pre-mutation state. Call before applying a change. */
  push(previous: Project, group?: string): void {
    const now = Date.now();
    if (group && group === this.lastGroup && now - this.lastTime < this.coalesceMs) {
      this.lastTime = now;
      return; // same gesture — keep the original snapshot
    }
    this.past.push(previous);
    if (this.past.length > this.cap) this.past.shift();
    this.future = [];
    this.lastGroup = group ?? null;
    this.lastTime = now;
  }

  /** Apply undo; returns the restored project or null if nothing to undo. */
  undo(current: Project): Project | null {
    if (this.past.length === 0) return null;
    this.future.push(current);
    this.lastGroup = null;
    return this.past.pop()!;
  }

  /** Apply redo; returns the restored project or null if nothing to redo. */
  redo(current: Project): Project | null {
    if (this.future.length === 0) return null;
    this.past.push(current);
    this.lastGroup = null;
    return this.future.pop()!;
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  clear(): void {
    this.past = [];
    this.future = [];
    this.lastGroup = null;
    this.lastTime = 0;
  }
}

/** Process-wide singleton. */
export const undoManager = new UndoManager();